import { FormEvent, useState } from "react";
import styled from "styled-components";
import {
  legacyReferralRetirementError,
  useLegacyReferralRetirement,
} from "../../hooks/api/admin/useLegacyReferralRetirement";
import { BACKGROUND_COLORS, BORDER_COLORS, COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { SPACING } from "../../constants/design/spacing";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";

const Card = styled.section`
  flex-shrink: 0;
  margin: ${SPACING.three}px 0;
  padding: ${SPACING.three}px;
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: 8px;
  background: ${BACKGROUND_COLORS.componentSolid};
  h2 { margin: 0 0 8px; font-size: 16px; }
  p { color: ${TEXT_COLORS.two}; margin: 8px 0 16px; line-height: 1.5; }
  form { display: flex; flex-wrap: wrap; gap: 16px; align-items: center; }
  label { display: flex; gap: 8px; align-items: center; }
`;

export function LegacyReferralRetirement() {
  const {
    data, error, isLoading, mutate, signedIn, submitting, complete,
    awaitingCheckpoint, submissionError, retireReferrals,
  } = useLegacyReferralRetirement();
  const [reviewed, setReviewed] = useState(false);
  const checkpoint = data?.retirement;
  const partialCheckpoint = Boolean(data?.retirement || data?.standard_perp_fee_reset) && !complete;
  const disabled = !signedIn || isLoading || !data || Boolean(error) ||
    data.preflight_error !== null || complete || partialCheckpoint || submitting || awaitingCheckpoint;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (disabled || !reviewed) return;
    await retireReferrals();
  };

  return (
    <Card aria-labelledby="legacy-referral-retirement-title">
      <h2 id="legacy-referral-retirement-title">Retire legacy referrals and reset STANDARD fees</h2>
      <p>
        Permanently close the old 10/10 referral program: existing legacy codes stop
        working and new legacy codes cannot be created. Custom usernames revert to
        generated Aevo names; existing generated names are retained. Legacy custom
        referral rebate and discount percentages are cleared.
      </p>
      <p>
        Set STANDARD perpetual base fees to 6.5 bps taker and 3.5 bps maker,
        replacing their custom perpetual base fees. Staking discounts still apply.
        Market makers and other account types, options, spot, margin settings and
        the new affiliates program are preserved.
      </p>
      <p>
        Existing 10/10 rewards remain claimable until 15 November 2026 at 00:00 UTC.
        Unclaimed rewards expire at that cutoff. This action requires both
        WRITE_SYSTEM and WRITE_CONFIG staff permissions.
      </p>
      {!signedIn && <p>Sign in with your staff account to view the cutover state.</p>}
      {signedIn && isLoading && <p role="status">Loading referral and fee cutover state…</p>}
      {error && (
        <p role="alert" style={{ color: COLORS.red.one }}>
          Unable to verify cutover state: {legacyReferralRetirementError(error)}
        </p>
      )}
      {data?.preflight_error && !complete && (
        <p role="alert" style={{ color: COLORS.red.one }}>
          Activation is blocked: {data.preflight_error}
        </p>
      )}
      {partialCheckpoint && (
        <p role="alert" style={{ color: COLORS.red.one }}>
          The referral retirement and fee reset checkpoints do not match. Activation
          is disabled; refresh the state and have the backend state reviewed.
        </p>
      )}
      {complete && checkpoint ? (
        <p role="status">
          Referral retirement and STANDARD fee reset confirmed at sequence {checkpoint.retired_seq},{" "}
          {new Date(checkpoint.retired_at_unix_nano / 1e6).toISOString()} (UTC).
        </p>
      ) : (
        <form onSubmit={submit}>
          <label htmlFor="legacy-referral-retirement-reviewed">
            <input
              id="legacy-referral-retirement-reviewed"
              type="checkbox"
              checked={reviewed}
              onChange={(event) => setReviewed(event.target.checked)}
              disabled={disabled}
            />
            I have reviewed the permanent referral retirement and STANDARD fee reset.
          </label>
          <Button
            type="submit"
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            disabled={disabled || !reviewed}
            style={{ height: 32, padding: "0 12px" }}
          >
            {submitting ? "Submitting cutover…" : "Retire legacy referrals and reset fees"}
          </Button>
        </form>
      )}
      {!complete && awaitingCheckpoint && (
        <p role="status">
          Waiting for matching backend referral and fee checkpoints. Do not submit
          another cutover; refresh the state to confirm the result.
        </p>
      )}
      {submissionError && (
        <p role="alert" style={{ color: COLORS.red.one }}>
          Cutover request: {submissionError}
        </p>
      )}
      <Button
        type="button"
        buttonTheme={ButtonThemeEnum.NEUTRAL2}
        disabled={!signedIn || submitting}
        onClick={() => mutate().catch(() => undefined)}
        style={{ height: 32, padding: "0 12px", marginTop: 12 }}
      >
        Refresh cutover state
      </Button>
    </Card>
  );
}
